import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import {
  runSessionRestorers,
  type SessionRestorer,
} from './runSessionRestorers';

type RestorerGetter = () => SessionRestorer;

interface SessionStateRestoreRegistry {
  register: (key: string, getRestorer: RestorerGetter) => () => void;
}

const SessionStateRestoreContext =
  createContext<SessionStateRestoreRegistry | null>(null);

interface SessionStateRestoreProviderProps {
  /** `olSession.socketId`: minted fresh on every reconnect, unlike `sessionId`. */
  socketId: string | undefined;
  children: ReactNode;
}

/**
 * Re-runs registered restorers whenever the olSession transport changes.
 *
 * olserver keeps view state (filter, ...) on a per-user mapview, which can be
 * reset while the web session survives. Features that own such state register
 * a restorer with `useRegisterSessionRestorer`. The transport at mount is the
 * baseline, so nothing runs on first login.
 */
export const SessionStateRestoreProvider = ({
  socketId,
  children,
}: SessionStateRestoreProviderProps) => {
  const registryRef = useRef(new Map<string, RestorerGetter>());
  const baselineSocketIdRef = useRef<string | undefined>(undefined);
  const generationRef = useRef(0);

  const registry = useMemo<SessionStateRestoreRegistry>(
    () => ({
      register: (key, getRestorer) => {
        registryRef.current.set(key, getRestorer);
        return () => {
          if (registryRef.current.get(key) === getRestorer) {
            registryRef.current.delete(key);
          }
        };
      },
    }),
    [],
  );

  useEffect(() => {
    if (!socketId) {
      return;
    }
    if (baselineSocketIdRef.current === undefined) {
      baselineSocketIdRef.current = socketId;
      return;
    }
    if (baselineSocketIdRef.current === socketId) {
      return;
    }
    baselineSocketIdRef.current = socketId;

    const generation = ++generationRef.current;
    const restorers = [...registryRef.current].map(
      ([key, getRestorer]) => [key, getRestorer()] as const,
    );
    runSessionRestorers(restorers, {
      isCurrent: () => generation === generationRef.current,
    });
  }, [socketId]);

  // Invalidates an in-flight run when the provider unmounts.
  useEffect(() => {
    const generation = generationRef;
    return () => {
      generation.current++;
    };
  }, []);

  return (
    <SessionStateRestoreContext.Provider value={registry}>
      {children}
    </SessionStateRestoreContext.Provider>
  );
};

interface UseRegisterSessionRestorerOptions {
  key: string;
  /** Idempotent; must reject on failure. Read current UI state at call time. */
  restore: () => Promise<void>;
  enabled?: boolean;
}

/**
 * Registers `restore` to run after an olSession reconnect. The latest
 * `restore`/`enabled` are used, so callers need no memoisation. No-op outside
 * `SessionStateRestoreProvider` (e.g. popup windows).
 *
 * `restore` must reach the session only through hooks rendered below the
 * provider (e.g. `useSetFilters`), whose effects run before the provider's in
 * the commit that carries the new socket. Capturing a session from a component
 * above the provider would send the restore over the old, closed socket.
 */
export const useRegisterSessionRestorer = ({
  key,
  restore,
  enabled = true,
}: UseRegisterSessionRestorerOptions): void => {
  const registry = useContext(SessionStateRestoreContext);
  const latestRef = useRef<SessionRestorer>({ restore, enabled });

  // Child effects run before the provider's effect in the same commit, so this
  // is already current when a socketId change triggers a run.
  useEffect(() => {
    latestRef.current = { restore, enabled };
  });

  useEffect(() => {
    if (!registry) {
      return;
    }
    return registry.register(key, () => latestRef.current);
  }, [registry, key]);
};
